import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import FavoriteButton from "@/components/feature/FavoriteButton";
import RelatedMiniCard from "@/components/feature/RelatedMiniCard";
import { allNovels, getNovelById, formatWordCount } from "@/utils/catalog";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function NovelDetailPage() {
  const { id } = useParams();
  const novel = getNovelById(id || "");
  const [showChapters, setShowChapters] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useDocumentTitle(
    novel ? `${novel.title} - 小说详情 - 17C.COM` : "小说详情 - 17C.COM",
    novel ? novel.summary : undefined
  );

  const triggerToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 1800);
  };

  if (!novel) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="小说内容不存在或已下架"
          description="你访问的小说可能已被移除，返回小说分类页看看其他作品吧。"
          actionLabel="返回小说分类"
          onAction={() => window.REACT_APP_NAVIGATE("/category/novel")}
        />
      </div>
    );
  }

  const related = allNovels.filter((n) => n.id !== novel.id && n.genre === novel.genre).slice(0, 5);

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6 pb-2">
        <Breadcrumb
          items={[{ label: "首页", to: "/" }, { label: "小说", to: "/category/novel" }, { label: novel.title }]}
        />
      </div>

      <div className="w-full px-4 md:px-8 pt-4 pb-10 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <div className="flex flex-col sm:flex-row gap-5 md:gap-7">
                <div className="w-full sm:w-52 shrink-0">
                  <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={novel.cover}
                      alt={novel.title}
                      title={`${novel.title} - 小说封面`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-label font-medium bg-primary-100 text-primary-700">
                    {novel.genre}
                  </span>
                  <h1 className="mt-3 font-heading text-2xl md:text-3xl font-black text-foreground-950">
                    {novel.title}
                  </h1>
                  <p className="mt-2 text-sm text-foreground-500 flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    作者：{novel.author}
                  </p>

                  <div className="mt-4 flex items-center gap-4 flex-wrap text-sm">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs ${
                        novel.status === "已完结"
                          ? "bg-secondary-100 text-secondary-800"
                          : "bg-accent-100 text-accent-800"
                      }`}
                    >
                      {novel.status}
                    </span>
                    <span className="text-foreground-600">{formatWordCount(novel.wordCount)}</span>
                    <span className="flex items-center gap-1 text-accent-700">
                      <i className="ri-thumb-up-line"></i>
                      推荐 {novel.recommend}
                    </span>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {novel.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-md text-xs bg-secondary-100 text-secondary-800">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <FavoriteButton kind="novel" id={novel.id} />
                    <button
                      type="button"
                      onClick={() => setShowChapters((v) => !v)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-list-ordered"></i>
                      {showChapters ? "收起目录" : "查看目录"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <h2 className="font-heading text-xl font-black text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                作品简介
              </h2>
              <p className="text-sm md:text-base leading-relaxed text-foreground-700 whitespace-pre-line">
                {novel.intro}
              </p>
            </div>

            {showChapters && (
              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 animate-fade-up">
                <h2 className="font-heading text-xl font-black text-foreground-950 mb-1 flex items-center gap-2">
                  <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                  章节目录预览
                </h2>
                <p className="text-xs text-foreground-500 mb-4">
                  以下为目录预览，章节阅读页为演示预留，暂未开放全文阅读。
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {novel.chapters.map((chapter) => (
                    <button
                      key={chapter}
                      type="button"
                      onClick={() => triggerToast("章节阅读页为演示预留，暂未开放")}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-background-100 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer text-left"
                    >
                      <i className="ri-file-text-line text-foreground-400"></i>
                      <span className="truncate">{chapter}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5 flex flex-wrap items-center gap-3 text-sm">
              <span className="text-foreground-600">所属分类：</span>
              <Link
                to="/category/novel"
                className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                小说
              </Link>
              <span className="inline-flex items-center gap-1 text-foreground-600">
                <i className="ri-price-tag-3-line"></i>
                {novel.genre}
              </span>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="font-heading text-lg font-black text-foreground-950 mb-2 flex items-center gap-2">
                <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                相关推荐
              </h2>
              {related.length > 0 ? (
                <div className="flex flex-col">
                  {related.map((item) => (
                    <RelatedMiniCard
                      key={item.id}
                      to={`/detail/novel/${item.id}`}
                      cover={item.cover}
                      title={item.title}
                      meta={`${item.author} · ${item.status}`}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-foreground-500 py-4">暂无相关推荐内容</p>
              )}
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}